/**
 * appClients.ts
 *
 * Wires the real application client registry.
 *
 * This is the ONLY service module that imports Pinia stores. Keeping the
 * store imports here rather than in `clients.ts` is what stops the
 * store -> clients -> authStore -> router import cycle from booting the
 * router whenever a store is imported.
 */
import { useAuthStore } from "@/stores/authStore";
import { createAuthTransport, createPublicTransport } from "@/services/transport";
import { type AppClients, createClients, setDefaultClients } from "@/services/clients";

const baseUrl = import.meta.env.VITE_GRPC_API_BASE_URL;

/**
 * The application's client registry.
 *
 * Stores are resolved lazily inside the accessors, so Pinia does not need
 * to be installed at the time this module is evaluated.
 */
export const appClients: AppClients = createClients({
  publicTransport: createPublicTransport(baseUrl),
  authTransport: createAuthTransport(baseUrl, {
    getAuthStore: () => useAuthStore(),
  }),
});

// Serves `useClients()` calls that happen outside a component injection
// context, e.g. a store first instantiated from a Connect interceptor.
setDefaultClients(appClients);
